import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import LogoutIcon from '@mui/icons-material/Logout'
import EventIcon from '@mui/icons-material/Event'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

export type EndConsultationDialogProps = {
  isEnding?: boolean
  onClose?: () => void
  onEndConsultation?: () => void
  onGenerateExpertise?: () => void
  open?: boolean
  onPlan?: () => void
  onQuit?: () => void
  /** Soft-warn the user when billing review is still pending */
  billingReviewPending?: boolean
  onReviewBilling?: () => void
  /** Group flow: how many patients still have unvalidated billing (switches to a group-specific message) */
  pendingBillingPatientCount?: number
}

const buttonStyle = {
  height: '60px',
}

export function EndConsultationDialog(props: EndConsultationDialogProps) {
  const {
    isEnding = false,
    onClose,
    onGenerateExpertise,
    onEndConsultation,
    onPlan,
    onQuit,
    open = false,
    billingReviewPending = false,
    onReviewBilling,
    pendingBillingPatientCount = 0,
  } = props
  const { t } = useTranslation()

  // Hard gate: while billing is pending, ending/quitting requires an explicit acknowledgement so
  // the user cannot click through without noticing. Reset on every open.
  const [billingAcknowledged, setBillingAcknowledged] = useState(false)
  useEffect(() => {
    if (open) setBillingAcknowledged(false)
  }, [open])
  const exitBlocked = billingReviewPending && !billingAcknowledged

  return (
    <Dialog
      PaperProps={{
        sx: {
          padding: '10px',
        },
      }}
      onClose={onClose}
      open={open}
    >
      <DialogContent>
        <Stack spacing={5}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
            <Typography fontWeight={'bold'}>{t('component.ui.endConsultationDialog.title')}</Typography>
            <Button onClick={onClose} color="error" variant="contained">
              {t('common:button.cancel.label')}
            </Button>
          </Stack>
          {billingReviewPending && (
            <Alert
              severity="warning"
              icon={<ReceiptLongIcon />}
              action={
                onReviewBilling ? (
                  <Button color="inherit" size="small" onClick={onReviewBilling}>
                    {t('component.ui.endConsultationDialog.billingPending.action', { defaultValue: 'Valider la facturation' })}
                  </Button>
                ) : undefined
              }
            >
              {pendingBillingPatientCount > 0
                ? t('component.ui.endConsultationDialog.billingPending.groupMessage', {
                    count: pendingBillingPatientCount,
                    defaultValue:
                      "La facturation n'a pas été finalisée pour {{count}} patient(s) du groupe. Chaque consultation doit être facturée individuellement.",
                  })
                : t('component.ui.endConsultationDialog.billingPending.message', {
                    defaultValue: "La facturation n'a pas encore été validée pour cette consultation.",
                  })}
            </Alert>
          )}
          {billingReviewPending && (
            <CheckboxInput
              id="billing-acknowledge-checkbox"
              checked={billingAcknowledged}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setBillingAcknowledged(event.target.checked)}
              label={t('component.ui.endConsultationDialog.billingPending.acknowledge', {
                defaultValue: "Je confirme vouloir quitter sans avoir finalisé la facturation",
              })}
            />
          )}
          <Stack direction="column" alignItems="start" justifyContent="space-around" spacing={3}>
            <Button onClick={onPlan} sx={buttonStyle} fullWidth variant="outlined">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                <span></span>
                <p>{t('component.ui.endConsultationDialog.planConsultationButton.label')}</p>
                <EventIcon />
              </Stack>
            </Button>

            <Button onClick={onGenerateExpertise} sx={buttonStyle} fullWidth variant="outlined">
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                <span></span>
                <p>{t('component.ui.endConsultationDialog.requestExpertiseButton.label')}</p>
                <ContactSupportIcon />
              </Stack>
            </Button>

            <Button
              sx={buttonStyle}
              fullWidth
              onClick={onEndConsultation}
              color="error"
              variant="outlined"
              disabled={exitBlocked || isEnding}
              data-testid="end-consultation-confirm"
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                <span></span>
                <p>{t('component.ui.endConsultationDialog.endConsultationButton.label')}</p>
                <LogoutIcon />
              </Stack>
            </Button>

            {onQuit && (
              <Button sx={buttonStyle} fullWidth onClick={onQuit} color="error" variant="outlined" disabled={exitBlocked}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <span></span>
                  <p>{t('component.ui.endConsultationDialog.quitConsultationButton.label')}</p>
                  <LogoutIcon />
                </Stack>
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default EndConsultationDialog
